import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Alert,
  Skeleton,
  Chip,
  Link,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PercentIcon from '@mui/icons-material/Percent';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import { useWorkOrderProfitabilityReport } from '../hooks/useReportsApi';

const DONUT_COLORS = ['#3182ce', '#10b981', '#94a3b8'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

const formatCurrencyCompact = (value) => {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
};

const STATUS_COLORS = {
  sent: 'info',
  paid: 'success',
  overdue: 'error',
};

function WorkOrderProfitabilitySection({ t, dateParams }) {
  const { data, loading, error } = useWorkOrderProfitabilityReport(dateParams);
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedWorkOrders = [...(data?.work_orders || [])].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal || '').toLowerCase();
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const donutData = (data?.margin_breakdown || [])
    .filter((item) => item.value > 0)
    .map((item) => ({
      name: t(`reports.profitability.${item.label}`),
      value: item.value,
    }));

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ExportButtons reportType="profitability/work-orders" params={dateParams} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={EuroIcon}
            label={t('reports.profitability.totalRevenue')}
            value={data?.total_revenue}
            loading={loading}
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={TrendingUpIcon}
            label={t('reports.profitability.partsMargin')}
            value={data?.total_parts_margin}
            loading={loading}
            format="currency"
            iconColor="#10b981"
            iconBgColor="rgba(16, 185, 129, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PercentIcon}
            label={t('reports.profitability.avgMarginPercent')}
            value={data?.avg_margin_percent}
            loading={loading}
            format="percent"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={ReceiptIcon}
            label={t('reports.profitability.avgPerWo')}
            value={data?.avg_revenue_per_wo}
            loading={loading}
            format="currency"
          />
        </Grid>
        {Number(data?.total_discount || 0) !== 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={LocalOfferIcon}
              label={t('reports.profitability.discountsGiven')}
              value={data?.total_discount}
              loading={loading}
              format="currency"
              iconColor="#f59e0b"
              iconBgColor="rgba(245, 158, 11, 0.1)"
            />
          </Grid>
        )}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.profitability.monthlyBreakdown')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (data?.monthly_breakdown || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.monthly_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period_label" />
                  <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="labor" name={t('reports.profitability.labor')} fill="#3182ce" stackId="a" />
                  <Bar dataKey="parts" name={t('reports.profitability.parts')} fill="#10b981" stackId="a" />
                  <Bar dataKey="other" name={t('reports.revenue.other')} fill="#f59e0b" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('reports.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.profitability.marginBreakdown')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : donutData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {donutData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value, entry) => `${value} (${formatCurrency(entry.payload?.value ?? 0)})`}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('reports.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Work Orders Table */}
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.profitability.workOrdersDetail')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <TableContainer>
            <Table size="small" sx={{ '& td, & th': { px: 1, whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 50 }}>
                    <TableSortLabel active={sortField === 'work_number'} direction={sortField === 'work_number' ? sortDirection : 'asc'} onClick={() => handleSort('work_number')}>
                      {t('reports.profitability.workNumber')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 80 }}>
                    <TableSortLabel active={sortField === 'date'} direction={sortField === 'date' ? sortDirection : 'asc'} onClick={() => handleSort('date')}>
                      {t('reports.profitability.date')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'normal', minWidth: 100 }}>{t('reports.profitability.customer')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'normal', minWidth: 80 }}>{t('reports.profitability.vehicle')}</TableCell>
                  <TableCell align="right">{t('reports.profitability.labor')}</TableCell>
                  <TableCell align="right">{t('reports.profitability.parts')}</TableCell>
                  <TableCell align="right">{t('reports.profitability.discount')}</TableCell>
                  <TableCell align="right">{t('reports.profitability.cost')}</TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={sortField === 'parts_margin'} direction={sortField === 'parts_margin' ? sortDirection : 'asc'} onClick={() => handleSort('parts_margin')}>
                      {t('reports.profitability.margin')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">{t('reports.profitability.marginPercent')}</TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={sortField === 'total_revenue'} direction={sortField === 'total_revenue' ? sortDirection : 'asc'} onClick={() => handleSort('total_revenue')}>
                      {t('reports.profitability.total')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t('reports.profitability.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedWorkOrders.map((wo, index) => {
                  const dateParts = wo.date.split('-');
                  const displayDate = `${parseInt(dateParts[2])}.${parseInt(dateParts[1])}.${dateParts[0]}`;
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => navigate(`/maintenances/${wo.maintenance_id}`)}
                          sx={{ fontWeight: 500 }}
                        >
                          {wo.work_number}
                        </Link>
                      </TableCell>
                      <TableCell>{displayDate}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal' }}>{wo.customer_name}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{wo.vehicle_reg}</Typography>
                        <Typography variant="caption" color="text.secondary">{wo.vehicle_make_model}</Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrencyCompact(wo.labor_revenue)}</TableCell>
                      <TableCell align="right">{formatCurrencyCompact(wo.parts_revenue)}</TableCell>
                      <TableCell align="right">{Number(wo.discount) !== 0 ? formatCurrencyCompact(wo.discount) : '-'}</TableCell>
                      <TableCell align="right">{wo.parts_cost > 0 ? formatCurrencyCompact(wo.parts_cost) : '-'}</TableCell>
                      <TableCell align="right">
                        <Typography
                          component="span"
                          sx={{ color: wo.parts_margin >= 0 ? 'success.main' : 'error.main', fontWeight: 500 }}
                        >
                          {formatCurrencyCompact(wo.parts_margin)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {wo.parts_margin_percent !== null ? `${wo.parts_margin_percent.toFixed(1)}\u00A0%` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrencyCompact(wo.total_revenue)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(`reports.profitability.invoiceStatus.${wo.invoice_status}`)}
                          color={STATUS_COLORS[wo.invoice_status] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {sortedWorkOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ color: 'text.secondary' }}>
                      {t('reports.noData')}
                    </TableCell>
                  </TableRow>
                )}
                {sortedWorkOrders.length > 0 && (
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell colSpan={4} sx={{ fontWeight: 700 }}>{t('reports.profitability.total')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrencyCompact(data?.total_labor || 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrencyCompact(data?.total_parts || 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{Number(data?.total_discount || 0) !== 0 ? formatCurrencyCompact(data.total_discount) : '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrencyCompact(data?.total_parts_cost || 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrencyCompact(data?.total_parts_margin || 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {data?.avg_margin_percent !== null ? `${data.avg_margin_percent.toFixed(1)}\u00A0%` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrencyCompact(data?.total_revenue || 0)}</TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default withTranslation()(WorkOrderProfitabilitySection);
